import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { ArrowRight, FileText, Scissors, Merge, Type, Image, PenTool, Minimize2, RotateCw } from 'lucide-react';

const toolData = {
  'merge-pdf': {
    title: 'Merge PDF Files Online Free',
    h1: 'Merge PDF Files',
    description: 'Combine multiple PDF files into one document in seconds. Free online PDF merger — no installation, no signup required.',
    keywords: 'merge pdf, combine pdf, join pdf files, pdf merger online free',
    icon: Merge,
    features: ['Drag and drop upload', 'Reorder pages before merging', 'No file size worries — up to 20MB', 'Instant download'],
  },
  'split-pdf': {
    title: 'Split PDF Online Free',
    h1: 'Split PDF Pages',
    description: 'Extract pages or split a PDF into multiple files. Free online PDF splitter — select pages, split, and download instantly.',
    keywords: 'split pdf, extract pdf pages, pdf splitter online free, separate pdf pages',
    icon: Scissors,
    features: ['Select specific pages to extract', 'Split into multiple documents', 'Preview pages before splitting', 'Instant processing'],
  },
  'edit-pdf': {
    title: 'Edit PDF Online Free — Add Text & Images',
    h1: 'Edit PDF Online',
    description: 'Add text, images, and annotations to any PDF. Free online PDF editor with precise WYSIWYG placement. No software needed.',
    keywords: 'edit pdf online, add text to pdf, pdf editor free, annotate pdf online',
    icon: Type,
    features: ['Add text with exact positioning', 'Insert images anywhere', 'Choose fonts, sizes, and colors', 'Works on mobile and desktop'],
  },
  'compress-pdf': {
    title: 'Compress PDF Online Free',
    h1: 'Compress PDF Files',
    description: 'Reduce PDF file size without losing quality. Free online PDF compressor — shrink files for email, upload, or storage.',
    keywords: 'compress pdf, reduce pdf size, pdf compressor online free, shrink pdf',
    icon: Minimize2,
    features: ['Smart compression algorithm', 'Maintains visual quality', 'Reduce file size up to 80%', 'No quality loss on text'],
  },
  'rotate-pdf': {
    title: 'Rotate PDF Pages Online Free',
    h1: 'Rotate PDF Pages',
    description: 'Rotate individual pages or entire PDF documents. Fix upside-down scans and adjust page orientation instantly.',
    keywords: 'rotate pdf, rotate pdf pages, flip pdf, pdf orientation',
    icon: RotateCw,
    features: ['Rotate 90°, 180°, or 270°', 'Rotate individual or all pages', 'Fix scanned document orientation', 'Preview before saving'],
  },
  'add-signature': {
    title: 'Add Signature to PDF Online Free',
    h1: 'Sign PDF Documents',
    description: 'Add your digital signature to any PDF document. Draw or type your signature and place it exactly where needed.',
    keywords: 'sign pdf, add signature to pdf, digital signature pdf, e-sign pdf free',
    icon: PenTool,
    features: ['Draw your signature', 'Place with pixel precision', 'Works on contracts and forms', 'Legally acceptable e-signature'],
  },
};

const ToolLanding = () => {
  const { toolSlug } = useParams();
  const navigate = useNavigate();
  const tool = toolData[toolSlug];

  if (!tool) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Tool not found</h1>
          <Link to="/"><Button>Go Home</Button></Link>
        </div>
      </div>
    );
  }

  const Icon = tool.icon;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <>
      <SEO
        title={tool.title}
        description={tool.description}
        keywords={tool.keywords}
        canonical={`${origin}/tools/${toolSlug}`}
      />
      <div className="min-h-screen">
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">{tool.h1}</h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">{tool.description}</p>
            <Button size="lg" onClick={() => navigate('/')} data-testid="tool-cta-button">
              Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        <section className="py-12 px-4 bg-muted/30">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-lg font-semibold text-center mb-8">Features</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {tool.features.map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-sm">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 text-center">
          <h2 className="text-lg font-semibold mb-2">All PDF Tools</h2>
          <p className="text-sm text-muted-foreground mb-6">Everything you need to work with PDFs</p>
          <div className="flex flex-wrap justify-center gap-2 max-w-xl mx-auto">
            {Object.entries(toolData).map(([slug, t]) => (
              <Link key={slug} to={`/tools/${slug}`} className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors">
                {t.h1}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export const toolSlugs = Object.keys(toolData);
export default ToolLanding;
